import { usePassportProvider } from "@/context";
import type { WalletGems } from "@/domain/gems-game/data-access";
import type {
  Rarity,
  RewardEarning,
} from "@/domain/gems-game/domain/lib/Earning";
import type {
  JackpotTier,
  SweepstakeResult,
} from "@/domain/gems-game/domain/lib/Sweepstake";
import { useGetWalletGems } from "@/hooks/api";
import { useAccessToken, useAccessTokenState } from "@/hooks/useAccessToken";
import { apiFetcher } from "@/utils/fetch";
import {
  authorizedFetcherGet,
  authorizedPost,
  optionalAuthorizedGet,
} from "@/utils/request";
import format from "date-fns-tz/format";
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import useSWR, { type BareFetcher, type KeyedMutator } from "swr";
import { RedeemTicketVideoModal } from "./Redemption/RedeemTicketVideoModal";
import { SweepStakePrizeModal } from "./Redemption/SweepStakePrizeModal";
import {
  type EligibilityResponse,
  type GroupedMythicReward,
  type MythicRewardResult,
  type MythicRewards,
  SweepStakeRewardTypes,
} from "./types";

export const DEFAULT_ENTRY_FEE = 500;

export interface PrizeConfig {
  weeklyFrom: string;
  weeklyTo: string;
  totalImx: number;
  maxMythic: number;
  minMythic: number;
  defaultGemEntry: number;
  jackpots: JackpotTier[];
}

export type WeeklyRewardsPeriod =
  | "REDEEM" // User can redeem a ticket for the current sweepstake
  | "CALCULATE" // Waiting for results after participating
  | "CLAIM" // User can claim their prize (previously called "FINAL")
  | "THANKS" // User has claimed their prize and is waiting for next sweepstake
  | "IDLE"; // Not participating or no active sweepstake

type WeeklyRewardsContext = {
  eligibility: EligibilityResponse | undefined;
  mutateEligibility: KeyedMutator<EligibilityResponse>;
  walletGems: WalletGems | undefined;
  mutateWalletGems: KeyedMutator<WalletGems>;
  currentPeriod: WeeklyRewardsPeriod;
  // For dev panel
  setDevOverridePeriod: (period: WeeklyRewardsPeriod | null) => void;
  // Legacy properties for backward compatibility
  isRedeemPeriod?: boolean;
  isFinalPeriod?: boolean;
  isCalculatePeriod?: boolean;
  isThanksPeriod?: boolean;
  isParticipatingInTheCurrentSweepstake: boolean;
  redeemTicket: () => Promise<void>;
  claimPrize: () => Promise<void>;
  currentSweepstakeEarning?: RewardEarning | null | undefined;
  sweepstakeTickets: RewardEarning[] | undefined;
  sweepstake: SweepstakeResult | undefined;
  latestClaimedTicket?: RewardEarning;
  jackpotWinners?: GroupedMythicReward[];
  isTicketsLoading: boolean;
  prizeConfig: PrizeConfig;
  hasFreeEntry: boolean;
  entryFee: number;
  hasEnoughGems: boolean;
  hasTicketUpgrade: boolean;
  isAccountFlagged: boolean;
  isJustClaimedReward: boolean;
  showPrizeModal: boolean;
  showRedemptionModal: boolean;
  setIsJustClaimedReward: (isJustClaimedReward: boolean) => void;
  refreshTickets: KeyedMutator<RewardEarning[]>;
  refreshSweepstake: KeyedMutator<SweepstakeResult>;
  isClaiming: boolean;
  isLoading: boolean;
  isEligibilityLoading: boolean;
  activityPeriod:
    | {
        from: string;
        to: string;
      }
    | undefined;
};

const weeklyRewardsContext = createContext<WeeklyRewardsContext | null>(null);

export function WeeklyRewardsProvider({ children }: PropsWithChildren) {
  const accessToken = useAccessTokenState();
  const getAccessToken = useAccessToken();
  const { walletAddress } = usePassportProvider();
  const { walletGems, mutate: mutateWalletGems } = useGetWalletGems(
    walletAddress ?? null,
    false,
  );
  const [isJustClaimedReward, setIsJustClaimedReward] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [redemptionRarity, setRedemptionRarity] = useState<
    Rarity | undefined
  >();
  const [devOverridePeriod, setDevOverridePeriod] =
    useState<WeeklyRewardsPeriod | null>(null);

  const {
    data: eligibility,
    mutate: mutateEligibility,
    isLoading: isEligibilityLoading,
  } = useSWR<EligibilityResponse>(
    walletAddress
      ? ["/v1/rewards/redemption/eligibility", walletAddress]
      : null,
    ([path, _]) =>
      authorizedFetcherGet<EligibilityResponse>(path, getAccessToken),
  );

  const { data: mythicRewards } = useSWR<MythicRewards>(
    "/v1/rewards/sweepstakes/mythic-rewards",
    apiFetcher,
  );

  const {
    data: sweepstake,
    mutate: refreshSweepstake,
    isLoading: isSweepstakeLoading,
  } = useSWR<SweepstakeResult>(() => "/v1/rewards/perpetual-rewards", (async (
    path: string,
  ) => {
    const token = await getAccessToken();
    return optionalAuthorizedGet(path, token);
  }) as BareFetcher<SweepstakeResult>);

  const [recentlyClaimedRewards, setRecentlyClaimedRewards] =
    useState<RewardEarning>();

  const {
    data: sweepstakeTicketPrizes,
    isLoading: isSweepstakeFreeEntryLoading,
    mutate: refreshTickets,
    isLoading: isTicketsLoading,
  } = useSWR<RewardEarning[]>(
    walletAddress
      ? [
          `/v1/rewards/earnings?reward_type=${SweepStakeRewardTypes.SWEEPSTAKE_FREE_ENTRY},${SweepStakeRewardTypes.SWEEPSTAKE_TICKET_UPGRADE},${SweepStakeRewardTypes.SWEEPSTAKE_TICKET}`,
          walletAddress,
        ]
      : null,
    ([path, _]) => authorizedFetcherGet(path, getAccessToken),
  );

  const sweepstakeTickets = useMemo(() => {
    return sweepstakeTicketPrizes?.filter(
      (x) => x.type === SweepStakeRewardTypes.SWEEPSTAKE_TICKET,
    );
  }, [sweepstakeTicketPrizes]);

  const isParticipatingInTheCurrentSweepstake = useMemo(() => {
    if (!sweepstakeTickets) return false;
    if (!sweepstake || !sweepstake.current) return false;

    // check if he has a ticket which is the current sweepstake
    return sweepstakeTickets
      .map((x) => x.source)
      .includes(sweepstake.current.trigger);
  }, [sweepstakeTickets, sweepstake]);

  const currentSweepstakeEarning = useMemo(() => {
    if (!sweepstakeTickets) return;
    if (!sweepstake || (!sweepstake.current && !sweepstake.previous)) return;

    const participatingTicket = sweepstake?.current
      ? sweepstakeTickets.find((x) => x.source === sweepstake?.current?.trigger)
      : sweepstake?.previous
        ? sweepstakeTickets.find(
            (x) => x.source === sweepstake?.previous?.trigger,
          )
        : null;

    return participatingTicket;
  }, [sweepstakeTickets, sweepstake]);

  const latestClaimedEarning = useMemo(() => {
    if (!sweepstakeTickets || sweepstakeTickets.length === 0)
      return currentSweepstakeEarning;
    if (recentlyClaimedRewards) {
      return sweepstakeTickets.find((x) => x.id === recentlyClaimedRewards.id);
    }
    return currentSweepstakeEarning;
  }, [sweepstakeTickets, currentSweepstakeEarning, recentlyClaimedRewards]);

  const latestClaimedTicket = useMemo(() => {
    if (!sweepstakeTickets || sweepstakeTickets.length === 0) return;
    if (recentlyClaimedRewards) {
      return sweepstakeTickets.find((x) => x.id === recentlyClaimedRewards.id);
    }
    return sweepstakeTickets
      .filter((ticket) => ticket.status === "claimed" && ticket.metadata)
      .sort((a, b) => {
        const dateA = new Date(a.claimed_at || 0).getTime();
        const dateB = new Date(b.claimed_at || 0).getTime();
        return dateB - dateA;
      })[0];
  }, [sweepstakeTickets, recentlyClaimedRewards]);

  const jackpotWinners = useMemo(() => {
    if (!mythicRewards) {
      return undefined;
    }

    const transformedRewards: GroupedMythicReward[] = [];

    for (const [questId, reward] of Object.entries(mythicRewards)) {
      type ResultsRecord = Record<string, MythicRewardResult[]>;
      const resultsByAmount = reward.results.reduce(
        (acc: ResultsRecord, result) => {
          if (!acc[result.amount]) {
            acc[result.amount] = [];
          }
          acc[result.amount].push(result);
          return acc;
        },
        {},
      );

      const sortedAmounts = Object.keys(resultsByAmount).sort(
        (a, b) => Number.parseFloat(b) - Number.parseFloat(a),
      );

      const groupedResults: MythicRewardResult[][] = sortedAmounts.map(
        (amount) => resultsByAmount[amount],
      );

      transformedRewards.push({
        startDate: reward.startDate,
        endDate: reward.endDate,
        questExternalID: questId,
        results: groupedResults,
      });
    }

    return transformedRewards.sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
    );
  }, [mythicRewards]);

  const isParticipatingInThePreviousSweepstake = useMemo(() => {
    if (!sweepstakeTickets) return false;
    if (!sweepstake || !sweepstake.previous) return false;

    // check if he has a ticket which is the previous sweepstake
    return sweepstakeTickets
      .map((x) => x.source)
      .includes(sweepstake.previous.trigger);
  }, [sweepstakeTickets, sweepstake]);

  // Determine the current period state based on the conditions
  const calculatedPeriod = useMemo((): WeeklyRewardsPeriod => {
    // THANKS Period - User has claimed their prize and is waiting for next sweepstake
    if (sweepstake && sweepstakeTickets && sweepstakeTickets.length > 0) {
      const participatingTicket = sweepstakeTickets?.find(
        (x) => x.source === sweepstake?.previous?.trigger,
      );
      if (
        (sweepstake.current === undefined &&
          isParticipatingInThePreviousSweepstake &&
          participatingTicket?.status === "claimed") ||
        isJustClaimedReward
      ) {
        return "THANKS";
      }
    }

    // CLAIM Period - User can claim their prize
    if (sweepstake?.previous) {
      // If has any ticket that is claimable, it should show that period
      const participatingTicket = sweepstakeTickets?.find(
        (x) => x.status === "claimable",
      );

      if (participatingTicket) {
        return "CLAIM";
      }
    }

    // CALCULATE Period - Waiting for results after participating
    if (sweepstake) {
      const now = new Date().getTime();
      const endDate = sweepstake.current
        ? new Date(sweepstake.current.endDate).getTime()
        : sweepstake.previous
          ? new Date(sweepstake.previous?.endDate).getTime()
          : new Date().getTime();

      const participatingTicket = sweepstake.current
        ? sweepstakeTickets?.find(
            (x) => x.source === sweepstake?.current?.trigger,
          )
        : sweepstake.previous
          ? sweepstakeTickets?.find(
              (x) => x.source === sweepstake?.previous?.trigger,
            )
          : null;

      if (
        (isParticipatingInThePreviousSweepstake ||
          isParticipatingInTheCurrentSweepstake) &&
        now <= endDate + 24 * 60 * 60 * 1000 &&
        participatingTicket?.status === "locked"
      ) {
        return "CALCULATE";
      }
    }

    // REDEEM Period - User can redeem a ticket for the current sweepstake
    if (sweepstake?.current) {
      const { startDate, endDate } = sweepstake.current;
      const now = new Date().getTime();
      const validFrom = new Date(startDate).getTime();
      const validTo = new Date(endDate).getTime();

      if (
        !isParticipatingInTheCurrentSweepstake &&
        now >= validFrom &&
        now <= validTo
      ) {
        return "REDEEM";
      }
    }

    // Default to IDLE state when no specific period is active
    return "IDLE";
  }, [
    sweepstake,
    isJustClaimedReward,
    sweepstakeTickets,
    isParticipatingInTheCurrentSweepstake,
    isParticipatingInThePreviousSweepstake,
  ]);

  // Use devOverridePeriod if it's set, otherwise use the calculated period
  const currentPeriod = useMemo(
    () => (devOverridePeriod !== null ? devOverridePeriod : calculatedPeriod),
    [devOverridePeriod, calculatedPeriod],
  );

  // Legacy period state getters for backward compatibility
  const isRedeemPeriod = useMemo(
    () => currentPeriod === "REDEEM",
    [currentPeriod],
  );

  const isCalculatePeriod = useMemo(
    () => currentPeriod === "CALCULATE",
    [currentPeriod],
  );
  const isFinalPeriod = useMemo(
    () => currentPeriod === "CLAIM",
    [currentPeriod],
  );
  const isThanksPeriod = useMemo(
    () => currentPeriod === "THANKS",
    [currentPeriod],
  );

  const isAccountFlagged = useMemo(() => {
    if (!eligibility) return false;
    return (
      eligibility.rules.is_not_sanctioned === false ||
      eligibility.rules.is_not_sybil === false
    );
  }, [eligibility]);

  const redeemTicket = useCallback(async () => {
    if (!accessToken) {
      console.error("No access token available");
      return;
    }

    try {
      const redeemed = await authorizedPost(
        "/v1/rewards/redemption",
        null,
        accessToken,
      );

      if (redeemed?.rarity) {
        setRedemptionRarity(redeemed.rarity);
        setShowRedemptionModal(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      // Invalidate SWR caches after claim
      refreshTickets();
      refreshSweepstake();
      mutateWalletGems();
    }
    return;
  }, [refreshTickets, refreshSweepstake, accessToken, mutateWalletGems]);

  const claimPrize = useCallback(async () => {
    setIsClaiming(true);
    if (!accessToken) return;

    // use ticket instead of the sweepstake date for redeem, because we might calculate early
    // Find any ticket that is claimable
    const participatingTicket = sweepstakeTickets?.find(
      (x) => x.status === "claimable",
    );

    // If the claimed ticket is not the current sweepstake then we need to set is just claimed
    // to true
    if (participatingTicket?.source !== sweepstake?.current?.trigger) {
      setIsJustClaimedReward(true);
      setRecentlyClaimedRewards(participatingTicket as RewardEarning);
    }

    try {
      await authorizedPost(
        `/v1/earnings/${participatingTicket?.id}/claim`,
        null,
        accessToken,
      );
      setShowPrizeModal(true);
    } catch (error) {
      console.error("Failed to claim prize", error);
    } finally {
      // Invalidate SWR caches after claim
      refreshTickets();
      refreshSweepstake();
      setIsClaiming(false);
    }
  }, [
    accessToken,
    refreshTickets,
    refreshSweepstake,
    sweepstake,
    sweepstakeTickets,
  ]);

  // Default if backend call fails just so that the ui isnt broken
  const defaultPrizeConfig = {
    weeklyFrom: "~",
    weeklyTo: "~",
    totalImx: 218923.072,
    maxMythic: 3333.333,
    minMythic: 77.777,
    defaultGemEntry: 500,
    jackpots: [],
    overallPrizeTypes: [
      "IMX",
      "Gems",
      "Free entry to following week's draw",
      "Increase of Reward Tier",
    ],
  };

  const prizeConfig: PrizeConfig = useMemo(() => {
    if (!sweepstake) return defaultPrizeConfig;
    const targetSweepstake = sweepstake.current || sweepstake.previous;
    if (!targetSweepstake) return defaultPrizeConfig;

    // From and to is in the format of "April 23th, 2025"
    const from = format(new Date(targetSweepstake.startDate), "MMMM do, yyyy");
    const to = format(
      new Date(
        new Date(targetSweepstake.endDate).getTime() + 2 * 24 * 60 * 60 * 1000,
      ),
      "MMMM do, yyyy",
    );

    let maxMythic = 0;
    let minMythic = 0;

    if (targetSweepstake.jackpots && targetSweepstake.jackpots.length > 0) {
      maxMythic = targetSweepstake.jackpots.reduce(
        (max, jackpot) => Math.max(max, Number(jackpot.imx_reward)),
        0,
      );

      minMythic = targetSweepstake.jackpots.reduce(
        (min, jackpot) => Math.min(min, Number(jackpot.imx_reward)),
        Number.MAX_SAFE_INTEGER,
      );
    }

    return {
      weeklyFrom: from,
      weeklyTo: to,
      jackpots: targetSweepstake.jackpots || [],
      totalImx:
        Number(targetSweepstake.totalIMX) === 0
          ? defaultPrizeConfig.totalImx
          : Number(targetSweepstake.totalIMX),
      maxMythic: maxMythic || defaultPrizeConfig.maxMythic,
      minMythic: minMythic || defaultPrizeConfig.minMythic,
      defaultGemEntry: targetSweepstake.gemsEntryAmount || 500,
    };
  }, [sweepstake]);

  const hasFreeEntry = useMemo(
    () =>
      sweepstakeTicketPrizes?.findIndex(
        (x) =>
          x.type === SweepStakeRewardTypes.SWEEPSTAKE_FREE_ENTRY &&
          !x.claimed_at,
      ) !== -1,
    [sweepstakeTicketPrizes],
  );

  const hasTicketUpgrade = useMemo(
    () =>
      sweepstakeTicketPrizes?.findIndex(
        (x) =>
          x.type === SweepStakeRewardTypes.SWEEPSTAKE_TICKET_UPGRADE &&
          !x.claimed_at,
      ) !== -1,
    [sweepstakeTicketPrizes],
  );

  const entryFee = useMemo(
    () =>
      hasFreeEntry
        ? 0
        : (sweepstake?.next?.gemsEntryAmount ??
          sweepstake?.current?.gemsEntryAmount ??
          DEFAULT_ENTRY_FEE),
    [
      hasFreeEntry,
      sweepstake?.next?.gemsEntryAmount,
      sweepstake?.current?.gemsEntryAmount,
    ],
  );

  const activityPeriod = useMemo(() => {
    const sw = sweepstake?.current || sweepstake?.previous;

    if (!sw) return;

    const from = format(new Date(sw.activityStartDate), "MMMM do, yyyy");
    const to = format(new Date(sw.activityEndDate), "MMMM do, yyyy");

    return {
      from,
      to,
    };
  }, [sweepstake]);

  const hasEnoughGems = useMemo(
    () =>
      (walletGems?.gems ?? 0) >=
      (sweepstake?.current?.gemsEntryAmount ?? DEFAULT_ENTRY_FEE),
    [walletGems?.gems, sweepstake?.current?.gemsEntryAmount],
  );

  const isLoading = useMemo(() => {
    return (
      isEligibilityLoading ||
      isSweepstakeLoading ||
      isTicketsLoading ||
      isSweepstakeFreeEntryLoading
    );
  }, [
    isEligibilityLoading,
    isSweepstakeLoading,
    isTicketsLoading,
    isSweepstakeFreeEntryLoading,
  ]);

  const values = useMemo(
    () => ({
      eligibility,
      mutateEligibility,
      walletGems,
      mutateWalletGems,
      currentPeriod,
      setDevOverridePeriod,
      refreshTickets,
      refreshSweepstake,
      isJustClaimedReward,
      setIsJustClaimedReward,
      // Legacy properties for backward compatibility
      isRedeemPeriod,
      isThanksPeriod,
      isFinalPeriod,
      isCalculatePeriod,
      isParticipatingInTheCurrentSweepstake,
      redeemTicket,
      currentSweepstakeEarning,
      claimPrize,
      sweepstakeTickets,
      isTicketsLoading,
      sweepstake,
      latestClaimedTicket,
      jackpotWinners,
      hasFreeEntry,
      hasTicketUpgrade,
      prizeConfig,
      entryFee,
      hasEnoughGems,
      isAccountFlagged,
      isClaiming,
      showPrizeModal,
      showRedemptionModal,
      isLoading,
      isEligibilityLoading,
      activityPeriod,
    }),
    [
      eligibility,
      mutateEligibility,
      walletGems,
      mutateWalletGems,
      currentPeriod,
      refreshTickets,
      refreshSweepstake,
      isJustClaimedReward,
      isRedeemPeriod,
      isCalculatePeriod,
      isThanksPeriod,
      hasTicketUpgrade,
      isFinalPeriod,
      isParticipatingInTheCurrentSweepstake,
      redeemTicket,
      claimPrize,
      currentSweepstakeEarning,
      sweepstakeTickets,
      isTicketsLoading,
      sweepstake,
      latestClaimedTicket,
      jackpotWinners,
      prizeConfig,
      hasFreeEntry,
      entryFee,
      hasEnoughGems,
      isAccountFlagged,
      isClaiming,
      showPrizeModal,
      showRedemptionModal,
      isLoading,
      isEligibilityLoading,
      activityPeriod,
    ],
  );

  return (
    <weeklyRewardsContext.Provider value={values}>
      {children}
      {showPrizeModal && latestClaimedEarning?.metadata && (
        <SweepStakePrizeModal
          earning={latestClaimedEarning}
          onClose={() => setShowPrizeModal(false)}
        />
      )}
      {showRedemptionModal && redemptionRarity && (
        <RedeemTicketVideoModal
          rarity={redemptionRarity}
          onClose={() => setShowRedemptionModal(false)}
        />
      )}
    </weeklyRewardsContext.Provider>
  );
}

export function useWeeklyRewards() {
  const ctx = useContext(weeklyRewardsContext);
  if (!ctx) {
    throw new Error(
      "useWeeklyRewards must be used within a WeeklyRewardsProvider",
    );
  }
  return ctx;
}
